import { useEffect, useState } from "react";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, langs } from "../../i18n";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    [t("nav.qualita"), "#qualita"],
    [t("nav.sostenibilita"), "#sostenibilita"],
    [t("nav.prodotti"), "#prodotti"],
    [t("nav.proprieta"), "#proprieta"],
  ];

  const Switcher = ({ light = false, testId = "lang-switcher" }) => (
    <div className="relative">
      <button
        data-testid={testId}
        onClick={() => setLangOpen(!langOpen)}
        className={`flex items-center gap-1.5 text-sm transition-colors ${light ? "text-[#F3EFE6]" : "text-[#29221D]"} hover:text-[#9A4930]`}
        aria-label="Language"
      >
        <Globe className="w-4 h-4" /> {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
      </button>
      {langOpen && (
        <div className="absolute right-0 mt-3 bg-[#FBF9F5] border border-[#E6DFD3] shadow-[0_20px_40px_-15px_rgba(41,34,29,0.3)] rounded-xl overflow-hidden z-50 min-w-[150px]">
          {langs.map(([code, name]) => (
            <button
              key={code}
              data-testid={`${testId}-opt-${code}`}
              onClick={() => { setLang(code); setLangOpen(false); }}
              className={`block w-full text-left px-5 py-2.5 text-sm hover:bg-[#F3EFE6] transition-colors ${lang === code ? "text-[#9A4930] font-semibold" : "text-[#29221D]"}`}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${
        scrolled ? "bg-[#FBF9F5]/80 backdrop-blur-xl border-[#E6DFD3] py-3" : "bg-transparent border-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20 flex items-center justify-between">
        <Logo full />
        <nav className="hidden md:flex items-center gap-8">
          {links.map(([label, href]) => (
            <a key={href} href={href} data-testid={`nav-${href.slice(1)}`} className="link-underline text-sm text-[#29221D]">
              {label}
            </a>
          ))}
          <Switcher testId="lang-switcher-desktop" />
          <a href="#contatti" data-testid="nav-cta-contact" className="btn-pill bg-[#29221D] text-[#FBF9F5]">
            {t("nav.contatti")}
          </a>
        </nav>
        <div className="flex items-center gap-3 md:hidden">
          <Switcher testId="lang-switcher-mobile" />
          <button data-testid="mobile-menu-toggle" className="p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#FBF9F5] border-t border-[#E6DFD3] px-6 py-6 flex flex-col gap-5">
          {[...links, [t("nav.contatti_link"), "#contatti"]].map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${href.slice(1)}`} className="font-serif text-2xl">
              {label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
};
